<template>
  <Modal
    ref="modalOccupationsUpload"
    :title="$t('OccupationsService.Modal.title')"
    width="60%"
    :zIndex="999"
    :has-tab="false"
  >
    <template v-slot:[`no-tab`]>
      <div class="modal-upload">
        <div class="modal-upload-info" v-show="!showForm && !loading">
          <p>
            {{ $t('OccupationsService.Modal.headerInstruction') }}
          </p>
          <!--
            Modelo por locale (como nos modais de people/groups): as abas de
            profissões dos 3 templates são Profissões/Occupations/Profesiones,
            com os headers traduzidos. O link fixo em /modelo.xlsx entregava a
            planilha em português para quem está em en/es.
          -->
          <a :href="_templateUrl" data-test-template download>{{
            $t('OccupationsService.Modal.downloadSheets')
          }}</a>
        </div>

        <!--
          `instruction` explicita: sem a prop, o AddFile cai no texto de
          PeopleService, que fala da coluna personID criando/atualizando "a
          pessoa" — errado no modal de profissoes
        -->
        <AddFile
          @file="validateFile"
          :label="fileName"
          :instruction="$t('OccupationsService.Modal.fileInstruction')"
        />

        <div v-if="showForm">
          <span class="modal-upload-subtitle">
            {{ $t('OccupationsService.Modal.subtitle') }}
          </span>
          <v-divider></v-divider>
        </div>

        <div v-if="loading" class="loading">
          <v-progress-circular indeterminate color="primary" />
        </div>

        <v-container fluid v-if="showForm">
          <v-radio-group v-model="action" inline class="modal-upload-action">
            <v-radio
              :label="$t('OccupationsService.Modal.labels.action.addUpdate')"
              :value="OCCUPATIONS_ACTIONS.AddUpdate"
            ></v-radio>
            <v-radio
              :label="$t('OccupationsService.Modal.labels.action.delete')"
              :value="OCCUPATIONS_ACTIONS.Delete"
            ></v-radio>
          </v-radio-group>
        </v-container>

        <v-form
          ref="form"
          @submit.prevent
          v-show="showForm"
          class="modal-upload-form"
          v-model="valid"
        >
          <v-container>
            <div class="single-input">
              <!--
                :key derivado do contador de limpeza (fix B3): quando a aba
                re-selecionada é a MESMA, a prop selectedItem não muda e o
                v-autocomplete esvaziado pelo form.reset() não re-sincroniza
                — o re-mount garante o select refletindo selectedTab
              -->
              <SingleSelection
                :key="`tab-select-${tabSelectKey}`"
                :selectedItem="selectedTab"
                :label="$t('OccupationsService.Modal.labels.selectTab')"
                :placeholder="$t('OccupationsService.Modal.labels.selectTab')"
                :items="tabs"
                @selectOption="handleTabSelection"
              />
            </div>
            <!--
              :key derivado da aba (mesmo padrão dos modais de people/groups):
              ao trocar de aba o form.reset() esvazia os v-autocomplete e o
              auto-match pode re-bindar a MESMA coluna — sem o re-mount a prop
              selectedItem não muda e o select fica visualmente vazio
            -->
            <div
              v-if="selectedTab && action === OCCUPATIONS_ACTIONS.AddUpdate"
              :key="`add-update-${selectedTab}`"
              class="inline-inputs"
            >
              <!--
                Contrato de colunas do backend
                (ParamsListSpreadsheedVerificationOccupationInformation):
                add-update exige SOMENTE title. 'ID' é opcional por design —
                numa planilha nova não existe — mas sem o bind toda linha do
                reimport vira criação. 'Descrição' é opcional.
              -->
              <div class="input-holder">
                <SingleSelection
                  :label="$t('OccupationsService.Modal.labels.selectIdColumn')"
                  :placeholder="
                    $t('OccupationsService.Modal.labels.selectIdColumn')
                  "
                  :items="occupationsColumns"
                  :selectedItem="_columnByAttribute.id || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'id')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="
                    $t('OccupationsService.Modal.labels.selectTitleColumn')
                  "
                  :placeholder="
                    $t('OccupationsService.Modal.labels.selectTitleColumn')
                  "
                  :items="occupationsColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.title || ''"
                  @selectOption="handleSelectOption($event, 'title')"
                />
              </div>
              <div class="input-holder">
                <SingleSelection
                  :label="
                    $t(
                      'OccupationsService.Modal.labels.selectDescriptionColumn'
                    )
                  "
                  :placeholder="
                    $t(
                      'OccupationsService.Modal.labels.selectDescriptionColumn'
                    )
                  "
                  :items="occupationsColumns"
                  :selectedItem="_columnByAttribute.description || ''"
                  :mandatory="false"
                  @selectOption="handleSelectOption($event, 'description')"
                />
              </div>
            </div>
            <div
              v-if="selectedTab && action === OCCUPATIONS_ACTIONS.Delete"
              :key="`delete-${selectedTab}`"
              class="inline-inputs"
            >
              <div class="input-holder">
                <SingleSelection
                  :label="$t('OccupationsService.Modal.labels.selectIdColumn')"
                  :placeholder="
                    $t('OccupationsService.Modal.labels.selectIdColumn')
                  "
                  :items="occupationsColumns"
                  :rules="[rules.required]"
                  :selectedItem="_columnByAttribute.id || ''"
                  @selectOption="handleSelectOption($event, 'id')"
                />
              </div>
            </div>
          </v-container>
        </v-form>

        <v-divider v-show="showForm && selectedTab"></v-divider>

        <div class="dialog-footer" v-show="showForm && selectedTab">
          <v-btn
            @click="handleDialog()"
            class="dialog-footer-btnCancel"
            depressed
            plain
          >
            {{ $t('OccupationsService.modalButtons.cancel') }}
          </v-btn>

          <v-btn
            @click="SendTabsInformations"
            class="dialog-footer-btn"
            depressed
          >
            {{ _buttonLabel }}
          </v-btn>
        </div>
      </div>
    </template>
  </Modal>
</template>
<script>
import { OCCUPATIONS_ACTIONS } from '@/helpers/upload-actions'
import {
  autoMatchColumns,
  OCCUPATIONS_SHEET_TAB_NAMES,
  OCCUPATIONS_COLUMN_CATALOG,
  OCCUPATIONS_AUTO_MATCH_ATTRIBUTES,
} from '@/helpers/column-auto-match'
import { uploadTabSelectionMixin } from '@/mixins/upload-tab-selection'
import { calcTemplateUrlByLocale } from '@/helpers/locale'
import AddFile from '@/views/SyncPage/Tabs/DataPage/Tabs/PeoplePage/Partials/UploadModal/Partials/AddFile/AddFile.vue'
import {
  verifySpreadSheet,
  sendTabsInformations,
} from '@/services/occupations/index'

export default {
  components: {
    AddFile,
  },
  mixins: [uploadTabSelectionMixin],
  data() {
    return {
      OCCUPATIONS_ACTIONS,
      // nomes esperados da aba do módulo (contrato do mixin de seleção) —
      // inclui as variantes pós-rename Ocupações/Occupations/Ocupaciones
      sheetTabCandidates: OCCUPATIONS_SHEET_TAB_NAMES,
      action: OCCUPATIONS_ACTIONS.AddUpdate,
      showForm: false,
      fileName: '',
      loading: false,
      columns: {},
      tabsInformations: {
        fileLocationId: '',
        tab: {
          name: '',
          columns: [],
        },
      },
      selectedTab: '',
      valid: false,
      rules: {
        required: v => {
          const requiredLabel = this.$t(
            'OccupationsService.Modal.requiredField'
          )

          if (v instanceof Array && v.length == 0) return requiredLabel
          return !!v || requiredLabel
        },
      },
    }
  },
  watch: {
    selectedTab() {
      this.reset()
      this.applyAutoMatch()
    },
    // Trocar a opção de importação limpa binds + aba e re-executa a
    // auto-seleção da aba (regras B2 no mixin), que re-dispara o auto-match
    // válido para a NOVA ação. O modal de profissões não tem flow — o
    // catálogo é único.
    action() {
      this.resetTabSelectionForOptionChange()
    },
  },
  computed: {
    // Profissões não tem fluxo legado/novo: o modelo é sempre o do locale
    _templateUrl() {
      return calcTemplateUrlByLocale(this.$i18n.locale)
    },
    tabs() {
      return this.columns.tabs?.map(el => el.name)
    },
    occupationsColumns() {
      return this.columns?.tabs?.find(el => el.name === this.selectedTab)
        ?.columns
    },
    // coluna mapeada por attribute — alimenta o selectedItem dos selects
    // (pré-preenchimento do auto-match e escolha manual ficam em sincronia)
    _columnByAttribute() {
      return this.tabsInformations.tab.columns.reduce(
        (map, { column, attribute }) => ({ ...map, [attribute]: column }),
        {}
      )
    },
    _buttonLabel() {
      return this.action === OCCUPATIONS_ACTIONS.AddUpdate
        ? this.$t('OccupationsService.modalButtons.loadOccupations')
        : this.$t('OccupationsService.modalButtons.deleteOccupations')
    },
  },
  methods: {
    initiliazeData() {
      this.action = OCCUPATIONS_ACTIONS.AddUpdate
      this.showForm = false
      this.fileName = ''
      this.columns = {}
      this.selectedTab = ''
      this.tabsInformations = {
        fileLocationId: '',
        tab: {
          name: '',
          columns: [],
        },
      }
    },
    reset() {
      // limpa também os binds acumulados — binds da aba/opção anterior não
      // podem vazar para o payload
      this.tabsInformations.tab.columns = []
      // fix B4 — o v-dialog do Modal renderiza lazy: antes da primeira
      // abertura o form não existe
      this.$refs.form?.reset()
    },
    // Seleção/auto-seleção de aba: uploadTabSelectionMixin
    // (handleTabSelection, autoSelectTab, resetTabSelectionFor*)
    // Auto-identificação de colunas: pré-preenche o bind coluna→atributo
    // quando o header casa com um nome conhecido (match exato pós-
    // normalização). Reusa handleSelectOption (dedupe e shape garantidos) e
    // NUNCA sobrescreve mapeamento existente — mesmo padrão dos modais de
    // people/groups.
    applyAutoMatch() {
      if (!this.selectedTab) return

      const mappedAttributes = this.tabsInformations.tab.columns.map(
        el => el.attribute
      )

      autoMatchColumns(
        this.occupationsColumns || [],
        OCCUPATIONS_COLUMN_CATALOG,
        OCCUPATIONS_AUTO_MATCH_ATTRIBUTES[this.action] || []
      )
        .filter(({ attribute }) => !mappedAttributes.includes(attribute))
        .forEach(({ column, attribute }) =>
          this.handleSelectOption({ value: column }, attribute)
        )
    },
    handleDialog() {
      this.initiliazeData()
      this.$refs.modalOccupationsUpload.handleDialog()
    },
    // fix B1 — dedupe por attribute (mesmo comportamento dos modais de
    // people/groups): re-selecionar uma coluna substitui o bind existente
    // do attribute em vez de acumular entradas duplicadas no payload
    handleSelectOption({ value }, name) {
      const remainingColumns = this.tabsInformations.tab.columns.filter(
        el => el.attribute !== name
      )

      // limpeza: remove o mapeamento do attribute
      if (value === null || value === undefined || value === '') {
        this.tabsInformations.tab.columns = remainingColumns
        return
      }

      this.tabsInformations.tab.columns = [
        ...remainingColumns,
        { column: value, attribute: name },
      ]
    },
    async validateFile(file) {
      // O guard vem ANTES do spinner: ligar loading e so depois descobrir que
      // nao ha arquivo deixava o modal preso no spinner para sempre (o bloco
      // de instrucoes fica oculto com `!showForm && !loading`, e nada mais
      // apagava a flag)
      if (!file) return

      this.loading = true
      this.fileName = file.name
      await verifySpreadSheet(file)
        .then(response => {
          this.loading = false
          this.showForm = true
          this.columns = response.data
          // Auto-seleção da aba pós-upload (limpa estado de upload
          // anterior antes de re-selecionar)
          this.resetTabSelectionForNewFile()
        })
        .catch(error => {
          this.loading = false
          this.fileName = ''
          this.showForm = false
          this.$emit('alert', {
            type: 'error',
            message: error?.response?.data?.message || this.$t('uploadError'),
          })
        })
    },
    async SendTabsInformations() {
      // O RETORNO de validate() e sincrono (Vuetify 2: VForm.validate()
      // devolve o resultado na hora). O v-model `valid` so e atualizado pelo
      // watcher de errorBag, que e um watcher do Vue e portanto assincrono —
      // no primeiro clique com um select obrigatorio vazio `valid` ainda era
      // `true` e o envio passava, para o backend recusar com
      // "PARAMS_NOT_SENT" depois do upload em vez de a tela marcar o campo.
      if (!this.$refs.form.validate()) {
        return
      }

      this.tabsInformations.action = this.action
      this.tabsInformations.fileLocationId = this.columns.fileLocationId

      this.loading = true
      this.showForm = false

      await sendTabsInformations(this.tabsInformations)
        .then(response => {
          this.$emit('alert', {
            type: 'success',
            message: response.data.message,
          })
          this.loading = false
          this.$emit('updateOccupationsList')
          this.handleDialog()
        })
        .catch(error => {
          this.loading = false
          this.showForm = true
          // Falha sem resposta HTTP (rede caiu, CORS, timeout) nao tem
          // error.response: acessar .data.message ali lancava TypeError
          // DENTRO do catch — rejeicao nao tratada e NENHUM feedback na tela.
          // Mesmo fallback do validateFile.
          this.$emit('alert', {
            type: 'error',
            message: error?.response?.data?.message || this.$t('uploadError'),
          })
        })
    },
  },
}
</script>

<style src="./style.scss" lang="scss" scoped></style>
